import { useState } from 'react';
import { formatDay, formatMoney } from './money';

export type PeriodStatus = 'pagado' | 'revision' | 'rechazado' | 'falta' | 'proximo';
export type PledgePeriod = { dueOn: string; status: PeriodStatus; paymentId: string | null; amountCents: number; hasFile: boolean; fileName: string | null; reviewNote: string | null };
export type Pledge = {
  id: string; campaignId: string; personId: string; amountCents: number; frequency: Frequency; startsOn: string; endsOn: string | null; note: string | null;
  status: 'activa' | 'cumplida' | 'cancelada'; statusNote: string | null; selfEnrolled: boolean;
  paidCount: number; dueCount: number; lateCount: number; paidCents: number; totalCents: number | null; totalCount: number | null; periods: PledgePeriod[];
};
export type Campaign = {
  id: string; name: string; description: string | null; visibility: 'publica' | 'privada'; goalCents: number | null; startsOn: string; endsOn: string | null;
  transferInfo: string | null; closed: boolean; people: number; paidCents: number; pledgedCents: number | null;
};
export type Frequency = 'semanal' | 'quincenal' | 'mensual' | 'anual';

export const FREQUENCY: Record<Frequency, string> = { semanal: 'Semanal', quincenal: 'Quincenal', mensual: 'Mensual', anual: 'Anual' };
export const EVERY: Record<Frequency, string> = { semanal: 'cada semana', quincenal: 'cada quincena', mensual: 'cada mes', anual: 'cada año' };
export const STATUS: Record<Pledge['status'], string> = { activa: 'Activa', cumplida: 'Cumplida', cancelada: 'Cancelada' };
const PERIOD_LABEL: Record<PeriodStatus, string> = { pagado: 'Entregada', revision: 'En revisión', rechazado: 'Rechazada', falta: 'Falta', proximo: 'Próxima' };
const PERIOD_ICON: Record<PeriodStatus, string> = { pagado: '✓', revision: '⏳', rechazado: '✗', falta: '○', proximo: '·' };

export const money = (c: number) => formatMoney(c, 'MXN');
const short = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'short' });

/** Avance general del propósito: lo entregado contra la meta (o contra lo prometido). */
export function CampaignProgress({ c }: { c: Campaign }) {
  const target = c.goalCents ?? c.pledgedCents;
  return (
    <div className="goal">
      <p className="small">
        <strong className="money">{money(c.paidCents)}</strong> entregado
        {c.goalCents ? <> de la meta de {money(c.goalCents)}</> : <span className="muted"> · sin meta fija</span>}
        {c.pledgedCents !== null && c.people > 0 ? <span className="muted"> · prometido {money(c.pledgedCents)}</span> : null}
        <span className="muted"> · {c.people === 1 ? '1 persona' : `${c.people} personas`}</span>
      </p>
      {target ? <progress className="usage" max={target} value={Math.min(c.paidCents, target)} aria-label={`Avance de ${c.name}`} /> : null}
      <p className="small muted">
        Desde el {formatDay(c.startsOn)} · {c.endsOn ? `hasta el ${formatDay(c.endsOn)}` : 'sin fecha de fin'}
        {c.closed ? ' · Cerrado' : ''}
      </p>
    </div>
  );
}

/** Resumen de una promesa: monto, frecuencia, avance y atrasos. */
export function PledgeSummary({ p }: { p: Pledge }) {
  return (
    <span className="small muted">
      {money(p.amountCents)} {EVERY[p.frequency]} · desde el {formatDay(p.startsOn)} · {p.endsOn ? `hasta el ${formatDay(p.endsOn)}` : 'indefinida'}
      {' · '}
      {p.paidCount} entregada{p.paidCount === 1 ? '' : 's'}
      {p.totalCount ? ` de ${p.totalCount}` : ''} ({money(p.paidCents)}
      {p.totalCents ? ` de ${money(p.totalCents)}` : ''})
      {p.lateCount ? <strong className="text-danger"> · {p.lateCount} pendiente{p.lateCount === 1 ? '' : 's'}</strong> : null}
      {p.status !== 'activa' ? ` · ${STATUS[p.status]}` : ''}
    </span>
  );
}

/** Las fechas de entrega como casillas: palomeadas, en revisión, rechazadas, faltantes y la próxima. */
export function PeriodChecks({ periods, onPick, label }: { periods: PledgePeriod[]; onPick?: (p: PledgePeriod) => void; label: (p: PledgePeriod) => string }) {
  const [all, setAll] = useState(false);
  const shown = all ? periods : periods.slice(-12);
  return (
    <div>
      <div className="pledge-periods">
        {shown.map((x) => (
          <button
            key={x.dueOn}
            type="button"
            className={`pledge-period pledge-period--${x.status}`}
            disabled={!onPick}
            title={x.reviewNote ? `${PERIOD_LABEL[x.status]}: ${x.reviewNote}` : PERIOD_LABEL[x.status]}
            aria-label={`${short(x.dueOn)}: ${PERIOD_LABEL[x.status]}. ${label(x)}`}
            onClick={() => onPick?.(x)}
          >
            <span aria-hidden="true">{PERIOD_ICON[x.status]}</span>
            <span>{short(x.dueOn)}</span>
          </button>
        ))}
      </div>
      {periods.length > 12 ? (
        <button type="button" className="link-button small" onClick={() => setAll(!all)}>
          {all ? 'Ver solo las últimas' : `Ver las ${periods.length} fechas`}
        </button>
      ) : null}
    </div>
  );
}

/** «Hasta» con casilla «Sin fecha de fin» (indefinida). Sin fecha, el campo no se envía. */
export function EndDateField({ id, defaultValue }: { id: string; defaultValue: string | null }) {
  const [none, setNone] = useState(!defaultValue);
  return (
    <div className="field">
      <label htmlFor={id}>Hasta</label>
      <input id={id} name="endsOn" type="date" disabled={none} defaultValue={defaultValue ?? ''} />
      <label className="check small">
        <input type="checkbox" checked={none} onChange={(e) => setNone(e.target.checked)} /> Sin fecha de fin
      </label>
    </div>
  );
}
